/**
 * Which forum thread a card came from (#54): the round robin, tag or lottery, the group
 * in it, the group's topic, a link to the thread and its moderator.
 *
 * Owner-only, all of it. The server leaves `ownerThread` off every card for a visitor
 * (models.Public), so every list built here from a visitor's cards comes out empty, and
 * the suggestions and auto-fill come from the owner's own cards rather than from anything
 * a visitor could ask the server for. There is no separate list of threads: the threads
 * are whatever she has typed on her cards.
 */
import { acquisitionLabel } from "./acquisition";
import { newestFirst, ratingStats } from "./card-ratings";
import type { RatedCard } from "./card-ratings";

export type OwnerThread = {
  thread: string;
  group: string;
  topic: string;
  link: string;
  moderator: string;
};

export const EMPTY_THREAD: OwnerThread = { thread: "", group: "", topic: "", link: "", moderator: "" };

/** The fields the helpers here read. Home.tsx and the ratings page both pass whole cards. */
export type ThreadedCard = RatedCard & {
  acquisitionType: string;
  acquisitionDetail: string;
  ownerThread?: OwnerThread;
};

/**
 * The one spelling of a thread name that matching goes by: case and extra spaces ignored,
 * so "WBMP RR", "wbmp  rr" and " WBMP RR " are one thread.
 */
export function threadKey(name: string) {
  return name.trim().replace(/\s+/g, " ").toLowerCase();
}

/** Whether a card has any thread detail at all worth drawing. */
export function hasThreadDetails(thread: OwnerThread | undefined): thread is OwnerThread {
  return Boolean(thread && (thread.thread || thread.group || thread.topic || thread.link || thread.moderator));
}

/**
 * The link as something safe to put in an href, or "" if it is not a web address. The
 * server refuses anything else already; this is the second lock on the same door.
 */
export function safeThreadLink(link: string) {
  return /^https?:\/\/[^\s/]+/i.test(link.trim()) ? link.trim() : "";
}

/** "356" reads as "Group 356"; a group she already wrote as "Group 356" is left alone. */
export function groupLabel(group: string) {
  const trimmed = group.trim();
  if (!trimmed) return "";
  return /^group\b/i.test(trimmed) ? trimmed : `Group ${trimmed}`;
}

/**
 * The values of one field across the cards, one per spelling-insensitive key, each spelled
 * as on the most recent card that has it, A–Z. What the thread and moderator boxes suggest.
 */
function distinctValues(cards: readonly ThreadedCard[], pick: (thread: OwnerThread) => string) {
  const seen = new Map<string, string>();
  for (const card of [...cards].sort(newestFirst)) {
    const value = card.ownerThread ? pick(card.ownerThread).trim() : "";
    const key = threadKey(value);
    if (key && !seen.has(key)) seen.set(key, value);
  }
  return [...seen.entries()]
    .map(([key, label]) => ({ key, label }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
}

/** Every thread she has used, for the typeahead and the gallery's thread filter. */
export function threadOptions(cards: readonly ThreadedCard[]) {
  return distinctValues(cards, (thread) => thread.thread);
}

/** Every moderator she has entered, for the moderator typeahead. */
export function moderatorOptions(cards: readonly ThreadedCard[]) {
  return distinctValues(cards, (thread) => thread.moderator);
}

/**
 * What the link and moderator boxes should hold once a thread name has been entered.
 *
 * Each comes from her most recent card in that thread (newest received, then newest
 * entered: the gallery's order) that has one, and only fills a box that is still empty —
 * anything she has typed stays as it is. A name she has not used before changes nothing.
 * The card being edited is left out, so it cannot fill itself.
 */
export function threadAutofill(
  cards: readonly ThreadedCard[],
  typedThread: string,
  current: Pick<OwnerThread, "link" | "moderator">,
  excludeId?: string,
): Pick<OwnerThread, "link" | "moderator"> {
  const key = threadKey(typedThread);
  const filled = { link: current.link, moderator: current.moderator };
  if (!key) return filled;
  const matches = cards
    .filter((card) => card.id !== excludeId && card.ownerThread && threadKey(card.ownerThread.thread) === key)
    .sort(newestFirst);
  if (!filled.link.trim()) filled.link = matches.find((card) => card.ownerThread?.link.trim())?.ownerThread?.link ?? filled.link;
  if (!filled.moderator.trim()) {
    filled.moderator = matches.find((card) => card.ownerThread?.moderator.trim())?.ownerThread?.moderator ?? filled.moderator;
  }
  return filled;
}

/** Whether a card belongs to the thread a filter names (a threadKey). */
export function inThread(card: ThreadedCard, key: string) {
  return threadKey(card.ownerThread?.thread ?? "") === key;
}

/** The label for cards with no thread in the lookup's "By thread" list. */
export const NO_THREAD = "No thread";

export type ThreadRow<C extends ThreadedCard = ThreadedCard> = {
  key: string;
  /** The thread as she last spelled it, or the public "how I got it" for cards with none. */
  label: string;
  /** False for the rows that gather cards with no thread. */
  isThread: boolean;
  /** The groups she was in, newest first, as written. */
  groups: string[];
  cards: C[];
  rated: number;
  unrated: number;
  average: number | null;
};

/**
 * One sender's cards, one row per thread, for the lookup's "By thread" panel.
 *
 * A row is a thread, not a thread and group: one person in one thread is usually one
 * group, and splitting by group would give a row per round with one card in each, which
 * is no average at all. The groups are listed on the row instead.
 *
 * Cards with no thread gather under their public "how I got it" ("Postcrossing Official",
 * "Postcrossing Forum · Round Robin"), or "No thread" if that is blank too. Threads come
 * first, most recent first; the rest follow, biggest first, with "No thread" last.
 */
export function threadRows<C extends ThreadedCard>(cards: readonly C[]): ThreadRow<C>[] {
  const groups = new Map<string, { isThread: boolean; cards: C[] }>();
  for (const card of [...cards].sort(newestFirst)) {
    const name = card.ownerThread?.thread.trim() ?? "";
    const key = name ? `thread:${threadKey(name)}` : `other:${acquisitionLabel(card) || NO_THREAD}`;
    const group = groups.get(key);
    if (group) group.cards.push(card);
    else groups.set(key, { isThread: Boolean(name), cards: [card] });
  }
  const rows = [...groups.entries()].map(([key, { isThread, cards: rowCards }]) => {
    const { rated, unrated, average } = ratingStats(rowCards);
    const seen = new Set<string>();
    const groupNames: string[] = [];
    for (const card of rowCards) {
      const group = card.ownerThread?.group.trim() ?? "";
      if (group && !seen.has(threadKey(group))) {
        seen.add(threadKey(group));
        groupNames.push(group);
      }
    }
    return {
      key,
      label: isThread ? rowCards[0].ownerThread!.thread.trim() : key.slice("other:".length),
      isThread,
      groups: groupNames,
      cards: rowCards,
      rated,
      unrated,
      average,
    };
  });
  const threads = rows.filter((row) => row.isThread);
  const others = rows.filter((row) => !row.isThread)
    .sort((a, b) => Number(a.label === NO_THREAD) - Number(b.label === NO_THREAD) ||
      b.cards.length - a.cards.length || a.label.localeCompare(b.label));
  return [...threads, ...others];
}
